import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import useEmblaCarousel from 'embla-carousel-react'
import classname from 'classnames'
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures'

import { useScreenWidth } from '../../hooks/use-screen-width'
import { SCREEN_SIZE_BREAKPOINTS } from '../../constants'
import {
  NextButton,
  PrevButton,
  usePrevNextButtons
} from './components/emblaCarouselArrowButtons'
import styles from './emblaCarousel.css'

/**
 * Base Embla carousel wrapper for caller-rendered slides.
 * Callers own slide markup and layout classes; this component owns Embla setup,
 * responsive navigation behavior, and shared arrow controls.
 *
 *  * Props:
 * - `slidesToShow`: Target number of slides visible in one viewport.
 * - `activeSlideCount`: Total number of currently renderable slides.
 * - `startSlideIndex`: Content index used to derive the initial snap position.
 * - `onClickNext`: Optional callback used to prefetch/load more items near the end.
 * - `emblaViewportCustomStyle`: Optional extra class for the viewport wrapper.
 * - `emblaButtonsCustomStyle`: Optional extra class for the arrow buttons wrapper.
 *
 * Key behaviors:
 * - Falls back to wheel-gesture support when no Embla plugins are provided.
 * - Hides arrow controls when all slides already fit in view.
 * - Emits `onClickNext` when the carousel moves forward in searching carousel.
 */

const EmblaCarousel = (props) => {
  const {
    options,
    plugins,
    onClickNext,
    startSlideIndex,
    slidesToShow,
    activeSlideCount,
    emblaViewportCustomStyle,
    emblaButtonsCustomStyle,
    children
  } = props
  const hideArrowButtonsByCount = slidesToShow >= (activeSlideCount || 0)
  const screenWidth = useScreenWidth()
  const isDesktopView = screenWidth > SCREEN_SIZE_BREAKPOINTS.MIN_480
  const hideArrowButtons = hideArrowButtonsByCount || !isDesktopView

  const resolvedPlugins = useMemo(() => {
    // Respect parent component provided Embla plugins; otherwise enable wheel gestures by default.
    if (plugins && plugins.length) {
      return plugins
    }

    return [WheelGesturesPlugin({ forceWheelAxis: 'x' })]
  }, [plugins])
  const resolvedOptions = useMemo(() => {
    // Base defaults for all standard Embla usages. Parent component can override via options.
    const baseOptions = {
      // Set explicitly because Embla merges reInit options with the previous configuration.
      active: true,
      // Looping keeps the carousel cycling back to the start after the last slide.
      loop: false,
      // Start alignment keeps the active slide pinned to the left edge.
      align: 'start',
      // Higher numbers enable slower scrolling animations. Recommended 20-60.
      duration: 50,
      // slidesToScroll controls how many slides move per navigation action.
      slidesToScroll: slidesToShow,
      // Drag-free allows the carousel to scroll freely so a tile can be displayed partially.
      dragFree: true,
      // containScroll trims extra scrolling space at the edges.
      containScroll: 'trimSnaps',
      ...options
    }

    // Initialize the carousel start index if startSlideIndex is provided.
    // This is used to highlight the current season or episode being viewed.
    const desktopScrollStep = Math.max(1, Math.floor(slidesToShow / 2))
    if (startSlideIndex !== undefined) {
      const scrollStep = isDesktopView ? desktopScrollStep : slidesToShow
      const startIndex = Math.floor(startSlideIndex / scrollStep)
      baseOptions.startIndex = startIndex
    }

    if (hideArrowButtonsByCount) {
      // When everything fits in view, deactivate Embla interactions.
      return { ...baseOptions, active: false }
    }

    if (isDesktopView) {
      // On larger screens, scroll half a viewport to keep navigation responsive but predictable.
      return {
        ...baseOptions,
        slidesToScroll: desktopScrollStep
      }
    }

    return baseOptions
  }, [options, hideArrowButtonsByCount, isDesktopView, slidesToShow, startSlideIndex])
  const [emblaRef, emblaApi] = useEmblaCarousel(resolvedOptions, resolvedPlugins)
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  useEffect(() => {
    if (!emblaApi || !onClickNext) {
      return undefined
    }

    /* Fires whenever the selected snap changes, regardless of whether navigation
    ** came from the arrow buttons, dragging, or trackpad wheel gestures.
    ** Prefetch when the visible window approaches the end to keep scrolling smooth.
    */
    const onSelect = () => {
      const PREFETCH_VIEWED_RATIO = 0.75
      const visible = emblaApi.slidesInView()
      const lastVisible = visible[visible.length - 1]

      if (lastVisible + slidesToShow >= activeSlideCount * PREFETCH_VIEWED_RATIO) {
        onClickNext()
      }
    }

    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onClickNext, slidesToShow, activeSlideCount])

  return (
    <div className={styles.embla}>
      <div
        className={classname(styles.emblaViewport, emblaViewportCustomStyle)}
        ref={emblaRef}
      >
        <div className={styles.emblaContainer}>
          {children}
        </div>
      </div>

      <div className={styles.emblaControls}>
        {/* Arrow controls are hidden on mobile and when all slides fit in view. */}
        {!hideArrowButtons && (
          <div className={classname(styles.emblaButtons, emblaButtonsCustomStyle)}>
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>
        )}
      </div>
    </div>
  )
}

EmblaCarousel.propTypes = {
  options: PropTypes.shape({
    loop: PropTypes.bool,
    align: PropTypes.string,
    slidesToScroll: PropTypes.number,
    duration: PropTypes.number
  }),
  plugins: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object
  ])),
  onClickNext: PropTypes.func,
  startSlideIndex: PropTypes.number,
  slidesToShow: PropTypes.number.isRequired,
  activeSlideCount: PropTypes.number.isRequired,
  emblaViewportCustomStyle: PropTypes.string,
  emblaButtonsCustomStyle: PropTypes.string,
  children: PropTypes.node.isRequired
}

EmblaCarousel.defaultProps = {
  onClickNext: undefined,
  options: {},
  plugins: [],
  startSlideIndex: 0,
  emblaViewportCustomStyle: '',
  emblaButtonsCustomStyle: ''
}

export default EmblaCarousel
